import { area_type_enum, class_of_use_enum, crrem_type_of_use_enum, type_of_use_enum } from '@predium/enums';
import { isNil, maxBy, sortBy, uniq } from 'lodash';
import { mapTypeOfUseToCRREM } from '../crrem/crrem-helpers';
import { accessEnum } from '../enums';
import { fFloatingPoint } from '../utils';
export type area = {
  value: number;
  area_type_id: string;
  type_of_use_id?: string;
  class_of_use_id?: string;
  [key: string]: unknown;
};

/**
 * Return the sum of areas matching the given area type (defaults to EBF)
 * @param areas array of areas
 * @param areaType area type to sum (defaults to EBF)
 * @returns number
 *
 */
export const getNetArea = (
  areas: Pick<area, 'value' | 'area_type_id'>[] | undefined,
  areaType: area_type_enum = area_type_enum.EBF,
): number => {
  if (isNil(areas)) return 0;
  const sum = areas.filter((a) => a.area_type_id === areaType).reduce((acc, area) => acc + area.value, 0);
  return fFloatingPoint(sum);
};

/**
 * Return the sum of all EBF areas
 * @param areas array of areas
 * @returns number
 *
 */
export const getEnergyReferenceAreaSum = (areas: Pick<area, 'value' | 'area_type_id'>[]): number => {
  const sum = areas.filter((a) => a.area_type_id === area_type_enum.EBF).reduce((acc, area) => acc + area.value, 0);
  return fFloatingPoint(sum);
};

/**
 * Return sum of the values of all areas
 * @param areas array of areas
 * @returns number
 */
export const getAreaSum = (areas: Pick<area, 'value'>[]): number => {
  const sum = areas.reduce((acc, area) => acc + area.value, 0);
  return fFloatingPoint(sum);
};

/**
 * Return the value of the area with the given type
 * @param areas array of areas
 * @param areaType area_type_enum
 * @returns number | null
 */
export const getValueAreaByType = (
  areas: Pick<area, 'value' | 'area_type_id'>[],
  areaType: area_type_enum,
): number | null => {
  const area = areas.find((a) => a.area_type_id === areaType);
  return area ? area.value : null;
};

/**
 * Return areas with the given type
 * @param areas array of areas
 * @param areaType area_type_enum
 * @returns area[]
 */
export const getAreaByType = <T extends area>(areas: T[], areaType: area_type_enum): T[] => {
  return areas.filter((a) => a.area_type_id === areaType);
};

/**
 * Return if a list of areas are mixed
 * @param areas array of areas
 * @returns true if a list of areas has multiple classes of use
 */
export const getBuildingIsMixed = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[] | undefined,
): boolean => {
  if (isNil(areas)) return false;
  const classesOfUse = getEbfClassesOfUse(areas);
  return classesOfUse.size > 1;
};

/**
 * Return types of use for all EBF areas
 * @param areas array of areas
 * @returns type_of_use_enum[]
 */
export const getEbfTypesOfUse = (
  areas: Required<Pick<area, 'type_of_use_id' | 'area_type_id'>>[] | undefined,
): type_of_use_enum[] => {
  if (isNil(areas)) return [];
  const typesOfUse = areas
    .filter((a) => a.area_type_id === area_type_enum.EBF)
    .map((a) => accessEnum(type_of_use_enum, a.type_of_use_id));
  return uniq(typesOfUse);
};

/**
 * Return the classes of use for an array of areas
 * @param areas areas table
 * @returns
 */
export const getEbfClassesOfUse = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[] | undefined,
): Set<class_of_use_enum> => {
  if (isNil(areas)) return new Set();

  const ebfAreas = areas.filter((area) => area.area_type_id === area_type_enum.EBF);
  const classesOfUse = new Set<class_of_use_enum>();
  ebfAreas.forEach((area) => {
    classesOfUse.add(accessEnum(class_of_use_enum, area.class_of_use_id));
  });
  return classesOfUse;
};

export const getAreaByTypeOfUse = (
  areas: Pick<area, 'value' | 'area_type_id' | 'type_of_use_id'>[],
): Record<Partial<type_of_use_enum>, number> => {
  const ebfAreas = areas.filter((area) => area.area_type_id === area_type_enum.EBF);
  const typeOfUseDistribution = {} as Record<Partial<type_of_use_enum>, number>;
  ebfAreas.forEach((area) => {
    if (!area.type_of_use_id) {
      throw new Error('No type of use found');
    }
    const typeOfUse = accessEnum(type_of_use_enum, area.type_of_use_id);
    const existingValue = typeOfUseDistribution[typeOfUse] ?? 0;
    typeOfUseDistribution[typeOfUse] = fFloatingPoint(existingValue + area.value);
  });
  return typeOfUseDistribution;
};

export const getAreaByCrremTypeOfUse = (
  areas: Pick<area, 'value' | 'area_type_id' | 'type_of_use_id'>[],
): Record<Partial<crrem_type_of_use_enum>, number> => {
  const ebfAreas = areas.filter((area) => area.area_type_id === area_type_enum.EBF);
  const typeOfUseDistribution = {} as Record<Partial<crrem_type_of_use_enum>, number>;
  ebfAreas.forEach((area) => {
    if (!area.type_of_use_id) {
      throw new Error('No type of use found');
    }
    const crremTypeOfUse = mapTypeOfUseToCRREM(accessEnum(type_of_use_enum, area.type_of_use_id));
    const existingValue = typeOfUseDistribution[crremTypeOfUse] ?? 0;
    typeOfUseDistribution[crremTypeOfUse] = fFloatingPoint(existingValue + area.value);
  });
  return typeOfUseDistribution;
};
export const isEuTaxonomyAvailable = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id' | 'type_of_use_id'>>[] | undefined,
): boolean => {
  if (isNil(areas)) return false;
  const typesOfUse = getEbfTypesOfUse(areas);
  if (typesOfUse.length === 0) {
    throw new Error('No types of use found');
  }
  if (typesOfUse.length === 1) return true;
  if (getBuildingIsMixed(areas)) return false;
  const classesOfUse = getEbfClassesOfUse(areas); // we know this is only one since it's not mixed
  // If the class of use is residential, we can use the EU taxonomy. Otherwise,
  // we can't since we have multiple types of use
  return classesOfUse.has(class_of_use_enum.RESIDENTIAL);
};

export const getBuildingHasAtLeastOneResidential = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[],
): boolean => {
  const classesOfUse = getEbfClassesOfUse(areas);
  return classesOfUse.has(class_of_use_enum.RESIDENTIAL);
};

export const getBuildingHasAtLeastOneCommercial = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[],
): boolean => {
  const classesOfUse = getEbfClassesOfUse(areas);
  return classesOfUse.has(class_of_use_enum.COMMERCIAL);
};

export const getAreaPerEbfClassOfUse = (
  areas: Required<Pick<area, 'class_of_use_id' | 'value' | 'area_type_id'>>[],
): Record<class_of_use_enum, number> => {
  const ebfAreas = areas.filter((area) => area.area_type_id === area_type_enum.EBF);
  const classOfUseDistribution: Record<class_of_use_enum, number> = {
    [class_of_use_enum.RESIDENTIAL]: 0,
    [class_of_use_enum.COMMERCIAL]: 0,
  };
  ebfAreas.forEach((area) => {
    if (area.class_of_use_id) {
      const classOfUse = accessEnum(class_of_use_enum, area.class_of_use_id);
      classOfUseDistribution[classOfUse] = fFloatingPoint(classOfUseDistribution[classOfUse] + area.value);
    }
  });
  return classOfUseDistribution;
};

export const getBuildingIsOnlyResidential = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[],
): boolean => {
  const classesOfUse = getEbfClassesOfUse(areas);
  return classesOfUse.size === 1 && classesOfUse.has(class_of_use_enum.RESIDENTIAL);
};

export const getBuildingIsOnlyCommercial = (
  areas: Required<Pick<area, 'class_of_use_id' | 'area_type_id'>>[] | undefined,
): boolean => {
  if (isNil(areas)) return false;
  const classesOfUse = getEbfClassesOfUse(areas);
  return classesOfUse.size === 1 && classesOfUse.has(class_of_use_enum.COMMERCIAL);
};

export type EnergyCertificateAreaLike = {
  area_type_id: string;
  type_of_use_id: string | null;
  value: number | null;
};

export const getCertificateAreaValue = (
  areas: EnergyCertificateAreaLike[],
  areaType: area_type_enum,
): number | null => {
  const values = areas
    .filter((area) => area.area_type_id === areaType)
    .map((area) => area.value)
    .filter((value): value is number => !isNil(value));
  if (values.length === 0) return null;
  return fFloatingPoint(values.reduce((acc, value) => acc + value, 0));
};

export const getCertificatePrimaryTypeOfUse = (areas: EnergyCertificateAreaLike[]): string | null => {
  const candidates = areas.filter((area) => !isNil(area.type_of_use_id) && area.area_type_id === area_type_enum.EBF);
  const primary = maxBy(candidates, (area) => area.value ?? Number.NEGATIVE_INFINITY);
  return primary?.type_of_use_id ?? null;
};

/**
 * Class of use governing lookups that only accept a single class per building (e.g. the UK
 * emission factors, which differ between residential and non-residential): the class holding
 * the larger EBF area share. Undefined when no EBF area carries a class of use.
 *
 * Ties break to the lexicographically smaller class id so the result is deterministic and
 * matches the SQL twin (`buildingMajorEbfClassOfUse` in the backend's common.queries.ts).
 */
export const getClassOfUseOfTheMajorEBF = (
  areas: { value: number; area_type_id: string; class_of_use_id?: string | null }[] | undefined,
): class_of_use_enum | undefined => {
  if (isNil(areas)) return undefined;
  const areaPerClassOfUse = new Map<class_of_use_enum, number>();
  areas
    .filter((a) => a.area_type_id === area_type_enum.EBF && !isNil(a.class_of_use_id))
    .forEach((a) => {
      const classOfUse = accessEnum(class_of_use_enum, a.class_of_use_id);
      areaPerClassOfUse.set(classOfUse, fFloatingPoint((areaPerClassOfUse.get(classOfUse) ?? 0) + a.value));
    });
  const major = sortBy(
    [...areaPerClassOfUse.entries()],
    ([, value]) => -value,
    ([classOfUse]) => classOfUse,
  )[0];
  return major?.[0];
};

export const getTypeOfUseOfTheMajorEBF = <T extends Pick<area, 'type_of_use_id' | 'area_type_id' | 'value'>>(
  areas: T[],
): type_of_use_enum => {
  const ebfAreas = getAreaByType(areas, area_type_enum.EBF);

  if (ebfAreas.length === 0) {
    throw new Error('No EBF areas found');
  }

  if (ebfAreas.length === 1) {
    return accessEnum(type_of_use_enum, ebfAreas[0]!.type_of_use_id);
  }

  return accessEnum(type_of_use_enum, maxBy(ebfAreas, (value) => value)!.type_of_use_id);
};
